import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { replace } from "react-router-redux";

import { useMediaQuery } from "@ui-kit/components/index.es";

import { getBoolean } from "util/config";
import * as configUtils from "util/config";
import * as dateUtils from "util/date";
import * as i18n from "util/i18n";
import * as schedulerUtils from "util/scheduler";
import { saveLocation } from "util/transaction";
import { getTransferExecutePath } from "util/v3UxConfig";
import { isUxTransfersImprovementsEnabled } from "util/v3ShellLayoutSync";
import { numberFormat } from "util/number";

import { saveDraft } from "middleware/form";
import * as transferMiddleware from "middleware/transfer";

import { actions as formActions } from "reducers/form";
import { actions as transactionLinesActions } from "reducers/form/transactionLines";
import { actions as frequentDestinationActions } from "reducers/frequentDestination";
import { actions as holidaysActions } from "reducers/holidays";
import { actions as templateActions } from "reducers/template";
import { actions as transferActions } from "reducers/transfer";

import {
    applyTransferTadHint,
    isTransferExecuteEntryPath,
    isValidAlias,
    matchTransferExecutePath,
    parseTransferAmount,
    resolveTransferAccountRef,
    TRANSFER_TO_CONTACT_FORM_ID,
} from "v3/pages/transfers/logic/transferExecuteShared";
import {
    buildTransferToContactValidationSchema,
    isTransferToContactContinueInactive,
} from "v3/pages/transfers/logic/transferExecuteValidation";

const ID_FORM = TRANSFER_TO_CONTACT_FORM_ID;
const ID_ACTIVITY = "transfers.toContact.send";

const INITIAL_VALUES = {
    debitAccount: "",
    transferKind: "",
    concept: "",
    creditAccount: "",
    amount: "",
    reference: "",
    sendEmail: false,
    email: "",
    emailText: "",
    scheduler: {},
};

const FORMIK_INITIAL_VALUES = {
    debitAccount: "",
    transferKind: "",
    concept: "",
    creditAccount: "",
    amount: "",
    reference: "",
    sendEmail: false,
    email: "",
    emailText: "",
};

/**
 * Business logic for to-contact transfer execute (legacy Form + v3 ui-kit page).
 *
 * @param {object} props
 * @returns {object}
 */
export default function useTransferToContactExecuteLogic(props) {
    const {
        dispatch,
        isDesktop,
        fetching,
        fetchingDraft,
        fetchingSubmit,
        location,
        accounts,
        summary,
        templates,
        isTemplatesVisible,
        setIsTemplatesVisible,
        setSchedulerButtonVisible,
        schedulerButtonVisible,
        isCVU,
        limit,
        limitUsed,
        limits,
        onCreateTemplate,
        listFrequentDestinations,
        lang,
        idEnvironment,
        isValidCbu,
        fetchingFrequentDestinations,
        isSameHolderTransfer,
        isSameHolderTransferForm,
        hideAmount,
        idTransaction,
        activeEnvironment,
        isRetailEnvironment,
        concepts,
    } = props;

    const [values, setValues] = useState(INITIAL_VALUES);
    const [draftButtonDisabled, setDraftButtonDisabled] = useState(false);
    const [formikProps, setFormikProps] = useState({});
    const [creditAccountConfirmed, setCreditAccountConfirmed] = useState(false);
    const [disabledMail, setDisabledMail] = useState(false);
    const [showFullList, setShowFullList] = useState(false);
    const [tadWarningPopupVisible, setTadWarningPopupVisible] = useState(false);
    const [tadWarningShowed, setTadWarningShowed] = useState(false);
    const tadWarningAcknowledgedRef = useRef(false);
    const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false);
    const [agendaListItems, setAgendaListItems] = useState([]);
    const limitsAccountRef = useRef();
    const isMobile = useMediaQuery("min", "md");

    const {
        debitAccount,
        creditAccount,
        amount,
        reference,
        email,
        emailText,
        sendEmail,
        transferKind,
        concept,
    } = values;

    const enableNewScheduleCard = configUtils.getBoolean("frontend.show.scheduleCards.functionalities", false);
    const enableHideAmounts = configUtils.getBoolean("frontend.hide.accounts.balance.funcionalities", false);
    const extendedAmountVariableEnabled = getBoolean("increase.size.amount.link.iso8583", false);
    const referenceMaxLength = configUtils.getInteger("frontend.transfers.reference.maxLength", 12);
    const tadWarningText = configUtils.get("frontend.TAD.warning.text");

    const parseAmount = parseTransferAmount;

    const getActivityIds = useCallback(() => ["transfers.toContact.send", "transfersCVU.toContact.send"], []);

    const requestAccountLimits = useCallback(
        (accountId) => {
            if (!accountId || limitsAccountRef.current === accountId) {
                return;
            }
            limitsAccountRef.current = accountId;
            dispatch(transferActions.accountLimitsListActivities(accountId, getActivityIds()));
        },
        [dispatch, getActivityIds],
    );

    const getActivityId = useCallback(() => ID_ACTIVITY, []);

    const getActivityIdTad = useCallback(() => "transfers.toContact.tad.send", []);

    const transferKindOptions = useMemo(
        () => [
            {
                value: "same",
                label: i18n.get("transfers.form.transfer.kind.same"),
            },
            {
                value: "other",
                label: i18n.get("transfers.form.transfer.kind.other"),
            },
        ],
        [],
    );

    const conceptsFiltered = useMemo(() => {
        if (isCVU) {
            return concepts
                .filter(({ id }) => !["BRH", "SON", "ROP", "OIH", "BNR", "APC", "SIS"].includes(id))
                .map(({ id, name }) => ({
                    value: id,
                    label: `${id} - ${name}`,
                }));
        }
        return concepts.map(({ id, name }) => ({
            value: id,
            label: `${id} - ${name}`,
        }));
    }, [concepts, isCVU]);

    const isTad = useCallback(
        (amountWithCurrency) => {
            const value = amountWithCurrency.amount;
            const environmentTypeTag = isRetailEnvironment ? "Retail" : "Corporate";
            const currencyTag = amountWithCurrency.currency.toString() === "0" ? "Pesos" : "Dolares";
            const minAmount = configUtils.getInteger(
                `frontend.TAD.minAmount${currencyTag}.${environmentTypeTag}`,
                100000,
            );
            const maxAmount = configUtils.getInteger(
                `frontend.TAD.maxAmount${currencyTag}.${environmentTypeTag}`,
                1000000,
            );

            if (isCVU) {
                return false;
            }

            if (activeEnvironment?.permissions?.transferDiferredAccreditation) {
                if (value >= minAmount && value <= maxAmount) {
                    return true;
                }
            }
            return false;
        },
        [activeEnvironment, isCVU, isRetailEnvironment],
    );

    const setHint = useCallback(
        (value) => {
            applyTransferTadHint(isTad(value), {
                setPopupVisible: setTadWarningPopupVisible,
                setShowed: setTadWarningShowed,
                isAcknowledged: () => tadWarningAcknowledgedRef.current,
                resetAcknowledged: () => {
                    tadWarningAcknowledgedRef.current = false;
                },
                onActive: () => {
                    setSchedulerButtonVisible(false);
                },
                onInactive: () => {
                    setSchedulerButtonVisible(true);
                },
            });
        },
        [isTad, setSchedulerButtonVisible],
    );

    const selectedAccount = useCallback(() => accounts.find((account) => account.idProduct === values.debitAccount), [
        accounts,
        values.debitAccount,
    ]);

    const getDefaultAccount = useCallback(() => {
        const summaryDebit = summary?.debitAccount;
        if (summaryDebit) {
            return summaryDebit.value || summaryDebit.idProduct || summaryDebit;
        }
        return accounts?.find((account) => account.favorite)?.idProduct || accounts[0]?.idProduct;
    }, [accounts, summary?.debitAccount]);

    const selectedCurrency = useCallback(() => {
        const selectedAccountForLabel = accounts.find((account) => account.idProduct === values.debitAccount);
        return selectedAccountForLabel?.currency ? i18n.get(`currency.label.${selectedAccountForLabel.currency}`) : "";
    }, [accounts, values.debitAccount]);

    const mapAccounts = useMemo(
        () =>
            accounts.map((account) => ({
                accountName: account.productAlias || account.shortLabel,
                amount: account.balance,
                currency: account.currency ? i18n.get(`currency.label.${account.currency}`) : "",
                description: account.productType ? i18n.get(`productType.${account.productType}`) : "",
                id: account.idProduct,
                codeBank: account.cbu?.substring(0, 3) || "319",
            })),
        [accounts],
    );

    const validationSchema = useMemo(() => buildTransferToContactValidationSchema(i18n.get, sendEmail), [sendEmail]);

    const isDraftButtonInactive = useMemo(() => !values.creditAccount || draftButtonDisabled || fetchingDraft, [
        draftButtonDisabled,
        fetchingDraft,
        values.creditAccount,
    ]);

    const isContinueInactive = useCallback(
        (formValues) =>
            isTransferToContactContinueInactive(formValues ?? {}, {
                creditAccountConfirmed,
                disabledMail,
                sendEmail,
            }),
        [creditAccountConfirmed, disabledMail, sendEmail],
    );
    const showInitialSpinner = useMemo(
        () => (fetching && !accounts.length) || !values.debitAccount || fetchingSubmit || fetchingFrequentDestinations,
        [accounts.length, fetching, fetchingFrequentDestinations, fetchingSubmit, values.debitAccount],
    );

    useEffect(() => {
        dispatch(templateActions.loadTemplateList(ID_FORM));
        dispatch(frequentDestinationActions.loadListRequest(idEnvironment, null));
    }, [dispatch, idEnvironment]);

    useEffect(() => {
        if (!fetchingFrequentDestinations) {
            setAgendaListItems(listFrequentDestinations);
        }
    }, [listFrequentDestinations, fetchingFrequentDestinations]);

    useEffect(() => {
        const { debitAccount: debitAccountDraft } = values;
        dispatch(transferActions.preActivity());
        dispatch(holidaysActions.listHolidays());
        const { cbu, email: summaryEmail, transferKind: summaryTransferKind, isFrequentDestinations } = summary;

        if (isTransferExecuteEntryPath(location?.pathname)) {
            saveLocation(props);
            if (location?.state) {
                const { isInternal, debitAccount: stateDebitAccount } = location.state;
                dispatch(transferActions.changeTransferGroup(isInternal));
                setValues((prev) => ({
                    ...prev,
                    debitAccount: stateDebitAccount,
                    creditAccount: "",
                    amount: {
                        amount: 0,
                        currency: "",
                    },
                    cbuOrAlias: "",
                    reference: "",
                    email: "",
                    emailText: "",
                    concept: "VAR",
                    transferKind: "",
                }));
                requestAccountLimits(stateDebitAccount);
            } else {
                const isInternalTrx =
                    summaryTransferKind === "other" ? false : activeEnvironment.permissions.transferInternal;

                if (!isInternalTrx && isFrequentDestinations) {
                    if (cbu) {
                        setValues((prev) => ({
                            ...prev,
                            cbuOrAlias: cbu,
                            email: summaryEmail,
                            transferKind: summaryTransferKind,
                        }));
                    }
                } else if (summaryEmail && isFrequentDestinations) {
                    setValues((prev) => ({
                        ...prev,
                        email: summaryEmail,
                    }));
                }
                dispatch(transferActions.changeTransferGroup(isInternalTrx));
            }
        } else if (matchTransferExecutePath(location?.pathname, "toContact", "modify") && debitAccountDraft) {
            requestAccountLimits(debitAccountDraft);
        }
        dispatch(
            frequentDestinationActions.setSameHolderTransferForm(
                (summary?.isFrequentDestinations && isSameHolderTransfer) || isSameHolderTransferForm,
            ),
        );
    }, []);

    useEffect(() => {
        const {
            transferKind: summaryTransferKind,
            debitAccount: summaryDebitAccount,
            creditAccount: summaryCreditAccount,
            reference: summaryReference,
            amount: summaryAmount,
            concept: summaryConcept,
            email: summaryEmail,
            emailText: summaryEmailText,
            sendEmail: summarySendEmail,
            cbu,
            alias,
            scheduler,
        } = summary;
        const debitAccountToUse = resolveTransferAccountRef(summaryDebitAccount);
        const creditAccountToUse = resolveTransferAccountRef(summaryCreditAccount) || cbu || alias;
        if (creditAccountToUse) {
            setCreditAccountConfirmed(true);
        }
        if (debitAccountToUse) {
            requestAccountLimits(debitAccountToUse);
        }
        setValues((prev) => ({
            ...prev,
            ...(debitAccountToUse ? { debitAccount: debitAccountToUse } : {}),
            ...(scheduler ? { scheduler } : {}),
            creditAccount: creditAccountToUse,
            amount: summaryAmount || "",
            transferKind: transferKindOptions.find(({ value }) => value === summaryTransferKind),
            concept: conceptsFiltered.find(({ value }) => value === summaryConcept) || conceptsFiltered[0],
            reference: summaryReference || "",
            email: summaryEmail || "",
            emailText: summaryEmailText || "",
            sendEmail: summarySendEmail,
        }));
    }, [summary]);

    useEffect(() => {
        if (fetchingSubmit || !amount || !debitAccount) {
            return;
        }
        const parsedAmountValue = parseAmount(amount);
        const foundAccount = accounts?.find((account) => account.idProduct === debitAccount);
        if (foundAccount) {
            const { currency } = foundAccount;
            if (parsedAmountValue > 0) {
                setHint({ amount: parsedAmountValue, currency });
            }
        }
    }, [accounts, amount, debitAccount, fetchingSubmit, parseAmount, setHint]);

    useEffect(() => {
        if (!selectedAccount()) {
            const defaultAccount = getDefaultAccount();
            if (defaultAccount) {
                setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
                requestAccountLimits(defaultAccount);
            }
        }
    }, [values.debitAccount]);

    useEffect(() => {
        if (!values.debitAccount) {
            const defaultAccount = getDefaultAccount();
            if (defaultAccount) {
                setValues((prev) => ({ ...prev, debitAccount: defaultAccount }));
                requestAccountLimits(defaultAccount);
            }
        }
    }, [values.debitAccount, accounts, summary?.debitAccount]);

    useEffect(() => {
        if (!values.concept && conceptsFiltered[0]) {
            setValues((prev) => ({ ...prev, concept: conceptsFiltered[0] }));
        }
    }, [conceptsFiltered, values.concept]);

    useEffect(() => {
        if (values.debitAccount && values.transferKind && creditAccountConfirmed && !showFullList) {
            setShowFullList(true);
        }
    }, [creditAccountConfirmed, showFullList, values.debitAccount, values.transferKind]);

    useEffect(() => {
        setDisabledMail(sendEmail && !values.email);
    }, [sendEmail, values.email]);

    useEffect(() => {
        if (!formikProps?.setValues) {
            return;
        }

        formikProps.setValues(
            {
                debitAccount,
                transferKind,
                concept,
                creditAccount,
                amount,
                reference,
                sendEmail,
                email,
                emailText,
            },
            true,
        );
    }, [
        amount,
        concept,
        creditAccount,
        debitAccount,
        email,
        emailText,
        formikProps,
        reference,
        sendEmail,
        transferKind,
    ]);

    const verifyCVU = useCallback(
        (value) => {
            if (isValidCbu(value)) {
                const isCVUvalue = value.startsWith("000");
                dispatch(transferActions.setIsCVU(isCVUvalue));
            } else if (isValidAlias(value)) {
                dispatch(transferActions.getCbuCvuFromAlias(value));
            }
        },
        [dispatch, isValidCbu],
    );

    const changeCbu = useCallback(
        (record) => {
            const transferKindDestination = record.isOwn === "1" ? "same" : "other";
            setValues((prev) => ({
                ...prev,
                cbuOrAlias: record.account,
                transferKind: transferKindDestination,
                email: record.email,
            }));
            verifyCVU(record.account);
        },
        [verifyCVU],
    );

    const closeTADModal = useCallback(() => {
        tadWarningAcknowledgedRef.current = true;
        setTadWarningPopupVisible(false);
    }, []);

    const openTADModal = useCallback(() => {
        setTadWarningPopupVisible(true);
    }, []);

    const handleDebitAccountSelect = useCallback(
        (accountId) => {
            setValues((prev) => ({ ...prev, debitAccount: accountId }));
            requestAccountLimits(accountId);
        },
        [requestAccountLimits],
    );

    const handleTransferKindSelect = useCallback((option) => {
        setValues((prev) => ({ ...prev, transferKind: option }));
    }, []);

    const handleConceptSelect = useCallback((option) => {
        setValues((prev) => ({ ...prev, concept: option }));
    }, []);

    const handleCreditAccountChange = useCallback((nextCreditAccount) => {
        setValues((prev) => ({ ...prev, creditAccount: nextCreditAccount }));
    }, []);

    const handleReferenceChange = useCallback((nextReference) => {
        setValues((prev) => ({ ...prev, reference: nextReference }));
    }, []);

    const handleSendEmailToggle = useCallback(() => {
        setValues((prev) => ({ ...prev, sendEmail: !prev.sendEmail }));
    }, []);

    const handleEmailChange = useCallback((nextEmail) => {
        setValues((prev) => ({ ...prev, email: nextEmail }));
    }, []);

    const handleEmailTextChange = useCallback((nextEmailText) => {
        setValues((prev) => ({ ...prev, emailText: nextEmailText }));
    }, []);

    const handleCreateTemplate = useCallback(
        (propsFormik) => {
            const account = accounts.find((accountItem) => accountItem.idProduct === values.debitAccount);
            const { currency } = account;
            onCreateTemplate({ ...propsFormik.values, currency });
        },
        [accounts, onCreateTemplate, values.debitAccount],
    );

    const handleChangeAmount = useCallback((nextAmount) => {
        setValues((prev) => ({ ...prev, amount: nextAmount }));
    }, []);

    const handleAccountSearch = useCallback(
        async (cbuCvuOrAlias) => {
            let cbuCvu;
            let alias;
            if (isValidCbu(cbuCvuOrAlias)) {
                cbuCvu = cbuCvuOrAlias;
            } else {
                alias = cbuCvuOrAlias;
            }
            return new Promise((resolve, reject) => {
                transferMiddleware
                    .getAccountByCbu(cbuCvu, alias)
                    .then((response) => {
                        const { clientUser, accountDetail, documentType } = response.data.data;
                        if (!clientUser && !accountDetail) {
                            reject(response.data.message);
                        }
                        resolve({
                            dataAccount: {
                                owner: clientUser.firstName,
                                document: clientUser.cuil,
                                documentType,
                                bankName: accountDetail.bankName,
                            },
                        });
                    })
                    .catch(() => {
                        reject(i18n.get("problems.finding.cbu"));
                    });
            });
        },
        [isValidCbu],
    );

    const handleTemplateSelect = useCallback(
        (template) => {
            const amountWithCurrency = template.amount;
            verifyCVU(template.cbuOrAlias);
            const isTadTrx = isTad(amountWithCurrency);
            setIsTemplatesVisible(false);
            setValues((prev) => ({
                ...prev,
                debitAccount: template.debitAccount,
                creditAccount: template.creditAccount || template.cbuOrAlias,
                transferKind: transferKindOptions.find(({ value }) => value === template.transferKind),
                concept: conceptsFiltered.find(({ value }) => value === template.concept),
                amount: template?.amount?.amount || 0,
                reference: template.reference,
                sendEmail: template.sendEmail,
                emailText: template.emailText,
                email: template.email,
                flagHint: isTadTrx,
                tadTrx: isTadTrx,
            }));

            dispatch(transactionLinesActions.loadTransactionLinesTemplateData(template));
            requestAccountLimits(template.debitAccount);
            setCreditAccountConfirmed(true);
        },
        [
            conceptsFiltered,
            dispatch,
            isTad,
            requestAccountLimits,
            setIsTemplatesVisible,
            transferKindOptions,
            verifyCVU,
        ],
    );

    const getDraftSummary = useCallback(
        (draftValues) => {
            const {
                debitAccount: draftDebitAccount,
                creditAccount: draftCreditAccount,
                amount: draftAmount,
                currency,
                reference: draftReference,
                sendEmail: draftSendEmail,
                email: draftEmail,
                emailText: draftEmailText,
                tadTrx,
                concept: draftConcept,
                transferKind: draftTransferKind,
                scheduler,
                flagHint,
            } = draftValues;
            const debitAccountId = resolveTransferAccountRef(draftDebitAccount);
            const creditAccountId = resolveTransferAccountRef(draftCreditAccount);
            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === debitAccountId);
            const schedulerToSend = scheduler && scheduler.selectedOption !== schedulerUtils.TODAY ? scheduler : null;
            const isCbu = () => /^\d+$/.test(creditAccountId);
            return {
                accounts,
                debitAccount: completeDebitAccount,
                cbu: isCbu() ? creditAccountId : null,
                alias: !isCbu() ? creditAccountId : null,
                currency,
                amount: draftAmount,
                reference: draftReference,
                sendEmail: draftSendEmail,
                email: draftEmail,
                emailText: draftEmailText,
                transferKind: draftTransferKind?.value || "",
                concept: draftConcept?.value,
                scheduler: schedulerToSend,
                idTransaction,
                tadTrx,
                flagHint,
            };
        },
        [accounts, idTransaction],
    );

    const onSaveDraft = useCallback(
        (draftValues) => {
            const {
                transferKind: draftTransferKind,
                concept: draftConcept,
                debitAccount: draftDebitAccount,
                creditAccount: draftCreditAccount,
            } = draftValues;

            const debitAccountId = resolveTransferAccountRef(draftDebitAccount);
            const creditAccountId = resolveTransferAccountRef(draftCreditAccount);
            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === debitAccountId);

            const parsedAmount = parseAmount(draftValues.amount) || 0;
            const tadTrx = isTad({
                currency: completeDebitAccount.currency,
                amount: parsedAmount,
            });
            dispatch(
                formActions.saveDraft({
                    idForm: ID_FORM,
                    idActivityDraft: tadTrx ? getActivityIdTad() : getActivityId(),
                    data: {
                        ...draftValues,
                        cbuOrAlias: creditAccountId,
                        amount: {
                            quantity: parsedAmount,
                            currency: completeDebitAccount.currency,
                        },
                        currency: completeDebitAccount.currency,
                        debitAccount: { value: debitAccountId },
                        creditAccount: { value: creditAccountId },
                        transferKind: draftTransferKind?.value || "",
                        concept: draftConcept?.value,
                    },
                    idTransaction: idTransaction || null,
                    scheduler: draftValues?.scheduler,
                    summary: getDraftSummary({
                        ...draftValues,
                        amount: parsedAmount,
                        currency: completeDebitAccount.currency,
                    }),
                }),
            );
        },
        [accounts, dispatch, getActivityId, getActivityIdTad, getDraftSummary, idTransaction, isTad, parseAmount],
    );

    const onSubmit = useCallback(
        ({
            debitAccount: submitDebitAccount,
            creditAccount: submitCreditAccount,
            amount: submitAmount,
            reference: submitReference,
            sendEmail: submitSendEmail,
            email: submitEmail,
            emailText: submitEmailText,
            concept: submitConcept,
            transferKind: submitTransferKind,
            flagHint,
        }) => {
            if (tadWarningPopupVisible) {
                return;
            }
            const { scheduler } = values;

            const debitAccountId = resolveTransferAccountRef(submitDebitAccount);
            const creditAccountId = resolveTransferAccountRef(submitCreditAccount);
            const completeDebitAccount = accounts.find(({ idProduct }) => idProduct === debitAccountId);
            const currency = parseInt(completeDebitAccount.currency, 10);
            const isCbu = () => /^\d+$/.test(creditAccountId);

            let schedulerToSend =
                scheduler && scheduler.selectedOption !== schedulerUtils.TODAY ? { ...scheduler } : null;
            if (schedulerToSend) {
                schedulerToSend = { ...schedulerToSend, newValueDate: schedulerToSend.valueDate };
                delete schedulerToSend.valueDate;
            }

            const tadTrx = isTad({
                currency,
                amount: parseAmount(submitAmount),
            });

            if (isDesktop) {
                dispatch(replace(getTransferExecutePath("toContact", { suffix: "back" })));
            }
            dispatch(
                transferActions.createTransferToContact(
                    {
                        accounts,
                        debitAccount: completeDebitAccount,
                        cbu: isCbu() ? creditAccountId : null,
                        alias: !isCbu() ? creditAccountId : null,
                        currency,
                        amount: parseAmount(submitAmount),
                        reference: submitReference,
                        sendEmail: submitSendEmail,
                        email: submitEmail,
                        emailText: submitEmailText,
                        concept: submitConcept.value,
                        transferKind: submitTransferKind.value,
                        scheduler: schedulerToSend,
                        idTransaction: summary.idTransaction,
                        tadTrx,
                        flagHint,
                        isCVU,
                    },
                    null,
                ),
            );
            if (matchTransferExecutePath(location?.pathname, "toContact", "modify")) {
                saveDraft();
            }
        },
        [
            accounts,
            dispatch,
            isCVU,
            isDesktop,
            isTad,
            location?.pathname,
            parseAmount,
            summary.idTransaction,
            tadWarningPopupVisible,
            values,
        ],
    );

    const handleSchedulerChange = useCallback((data) => {
        if (data.frequency === schedulerUtils.ONCE) {
            setValues((prev) => ({
                ...prev,
                scheduler: {
                    selectedOption: data.frequency,
                    valueDate: data.startsOn,
                    program: null,
                },
            }));
        } else {
            const newProgramData = {
                frequency: data.frequency === schedulerUtils.CUSTOM ? data.customFreq : data.frequency,
                frequencyValue: data.frequency === schedulerUtils.CUSTOM ? data.customFreqValue : "1",
                days: dateUtils.getDayFromDate(data.startsOn),
                day: data.startsOn.getDate(),
                occurrence: null,
                startsOn: data.startsOn,
                lapse: data.lapse.lapse,
                number: data.lapse.number,
                date: data.lapse.date,
                isCustom: data.frequency === schedulerUtils.CUSTOM,
                selectedOption: data.frequency,
            };
            if (newProgramData.frequency === schedulerUtils.WEEK && newProgramData.isCustom) {
                newProgramData.days = data.days;
            }
            if (newProgramData.frequency === schedulerUtils.MONTH && data.occurrenceType === "occurrence") {
                newProgramData.occurrence = dateUtils.weekOfMonth(moment(data.startsOn));
                newProgramData.day = null;
            }
            setValues((prev) => ({
                ...prev,
                scheduler: {
                    selectedOption: newProgramData.frequency,
                    program: newProgramData,
                },
            }));
        }
        setIsScheduleModalVisible(false);
    }, []);

    const openScheduleModal = useCallback(() => {
        setIsScheduleModalVisible(true);
    }, []);

    const closeScheduleModal = useCallback(() => {
        setIsScheduleModalVisible(false);
    }, []);

    const customHandleKeyDown = useCallback(
        (e) => {
            const { decimalSeparator } = numberFormat(lang);
            const { value, selectionStart, selectionEnd } = e.target;
            const integerPart = value.split(decimalSeparator)[0].replace(/\D/g, "");
            const extendedAmountEnabled = getBoolean("increase.size.amount.link.iso8583", false);
            const maxSize = extendedAmountEnabled ? 15 : 10;

            const allowedKeys = [
                "Backspace",
                "Delete",
                "ArrowLeft",
                "ArrowRight",
                "Tab",
                "Home",
                "End",
                "Control",
                "Meta",
                "Shift",
            ];

            if (e.ctrlKey || e.metaKey || e.shiftKey) {
                return;
            }

            const isAllSelected = selectionStart === 0 && selectionEnd === value.length;
            const decimalIndex = value.indexOf(decimalSeparator);

            if (
                (e.key === decimalSeparator || e.key === ".") &&
                (selectionStart <= decimalIndex || decimalIndex === -1) &&
                integerPart.length >= maxSize
            ) {
                return;
            }

            if (isAllSelected) {
                return;
            }

            if (
                integerPart.length >= maxSize &&
                (selectionStart <= decimalIndex || decimalIndex === -1) &&
                !allowedKeys.includes(e.key)
            ) {
                e.preventDefault();
            }
        },
        [lang],
    );

    const handleContactSelect = useCallback(
        (contact) => {
            const { cbu, isOwn, recipientEmail } = contact;
            const kind = isOwn === "1" ? "same" : "other";
            const selectedTransferKind = transferKindOptions.find(({ value }) => value === kind);
            const contactEmail = recipientEmail || undefined;
            setValues((prev) => ({
                ...prev,
                creditAccount: cbu,
                cbuOrAlias: cbu,
                email: contactEmail,
                transferKind: selectedTransferKind,
            }));
            verifyCVU(cbu);
            setCreditAccountConfirmed(true);
        },
        [transferKindOptions, verifyCVU],
    );

    const handleAgendaOneContactSelect = useCallback(
        (cbuCvu) => {
            const contact = listFrequentDestinations.find((contactItem) => contactItem.cbu === cbuCvu);
            handleContactSelect(contact);
        },
        [handleContactSelect, listFrequentDestinations],
    );

    const agendaItems = useMemo(
        () =>
            agendaListItems?.map((contact) => ({
                cbucvu: contact.cbu,
                isOwn: contact.isOwn,
                name: contact.recipientName,
                email: contact.recipientEmail,
                reference: contact.name,
                document: contact.recipientDocumentNumber,
                onClick: () => handleContactSelect(contact),
            })),
        [agendaListItems, handleContactSelect],
    );

    const handleSameHolderFilterChange = useCallback(
        (value) => {
            dispatch(frequentDestinationActions.setSameHolderTransferForm(value));
        },
        [dispatch],
    );

    const handleSaveDraftClick = useCallback(
        (propsFormik) => {
            if (!isUxTransfersImprovementsEnabled()) {
                setDraftButtonDisabled(true);
            }
            onSaveDraft(propsFormik.values);
        },
        [onSaveDraft],
    );

    const handleAmountBlur = useCallback(
        (nextAmount) => {
            handleChangeAmount(nextAmount);
        },
        [handleChangeAmount],
    );

    const agendaInitialSameHolderFilter = useMemo(
        () => (summary?.isFrequentDestinations && isSameHolderTransfer) || isSameHolderTransferForm,
        [isSameHolderTransfer, isSameHolderTransferForm, summary?.isFrequentDestinations],
    );

    return {
        idForm: ID_FORM,
        idActivity: ID_ACTIVITY,
        values,
        setValues,
        debitAccount,
        creditAccount,
        amount,
        reference,
        email,
        emailText,
        sendEmail,
        transferKind,
        concept,
        scheduler: values.scheduler,
        draftButtonDisabled,
        isDraftButtonInactive,
        creditAccountConfirmed,
        setCreditAccountConfirmed,
        disabledMail,
        isContinueInactive,
        showFullList,
        formikProps,
        setFormikProps,
        initialValues: FORMIK_INITIAL_VALUES,
        validationSchema,
        tadWarningPopupVisible,
        setTadWarningPopupVisible,
        tadWarningShowed,
        setTadWarningShowed,
        isScheduleModalVisible,
        setIsScheduleModalVisible,
        schedulerButtonVisible,
        isMobile,
        isDesktop,
        extendedAmountVariableEnabled,
        enableHideAmounts,
        enableNewScheduleCard,
        referenceMaxLength,
        tadWarningText,
        hideAmount,
        limit,
        limits,
        limitUsed,
        templates,
        isTemplatesVisible,
        setIsTemplatesVisible,
        summary,
        fetching,
        fetchingDraft,
        fetchingSubmit,
        fetchingFrequentDestinations,
        showInitialSpinner,
        mapAccounts,
        transferKindOptions,
        conceptsFiltered,
        agendaListItems,
        agendaItems,
        agendaInitialSameHolderFilter,
        listFrequentDestinations,
        isSameHolderTransfer,
        isSameHolderTransferForm,
        selectedAccount,
        selectedCurrency,
        parseAmount,
        isTad,
        isValidAlias,
        isValidCbu,
        verifyCVU,
        changeCbu,
        closeTADModal,
        openTADModal,
        getActivityId,
        getActivityIdTad,
        getActivityIds,
        handleDebitAccountSelect,
        handleTransferKindSelect,
        handleConceptSelect,
        handleCreditAccountChange,
        handleCreateTemplate,
        handleChangeAmount,
        handleReferenceChange,
        handleSendEmailToggle,
        handleEmailChange,
        handleEmailTextChange,
        handleAccountSearch,
        handleTemplateSelect,
        handleContactSelect,
        handleAgendaOneContactSelect,
        handleSameHolderFilterChange,
        getDraftSummary,
        onSaveDraft,
        onSubmit,
        handleSchedulerChange,
        openScheduleModal,
        closeScheduleModal,
        customHandleKeyDown,
        handleSaveDraftClick,
        handleAmountBlur,
    };
}
